import React, { useEffect, useMemo, useState } from 'react'
import { Button, Icon, MenuItem } from '@material-ui/core'
import { Checkbox } from '@mui/material'
import moment from 'moment'
import { toast } from 'react-toastify'
import Select from 'app/components/reusable/Select'
import { SearchField } from 'app/reusable/form/components/Select'
import { Loading } from 'app/components/SimpleList/SimpleList'
import useDjangoList from 'app/hooks/useDjangoList'
import HttpHandler, { getModelChangeEvent } from 'app/reusable/HttpHandler'
import { useDismissModal } from 'app/ducks/modal/hooks'
import {
  AD_HOC_REASON_LABELS,
  AD_HOC_REASON_OPTIONS,
} from 'app/views/attendance/constants'

// What's built from roll calls and reloads when one is saved: the
// `ModelListPage` reports (amsm-backend's attendance list endpoints, see
// `views/attendance/AttendanceReport`) and the teacher area's class list and
// indicators (see `views/school/TeacherArea`).
const ROLL_CALL_REPORT_MODELS = [
  'attendance-ranking',
  'class-occupancy',
  'teacher-classes',
  'teacher-area',
]

const formatClassDate = (date) => moment(date).format('ddd, DD/MM/YYYY')

/**
 * The class roll call ("chamada"), made for the teacher's phone: pick one of
 * the class's recent dates, tap each student present, save once. Saving
 * replaces the whole roll call of that date, so a student marked by mistake
 * can be un-marked. The student list and dates come from the backend
 * (`activities/api/activity-class/:id/roll-call`): enrollments for fixed
 * classes, that date's bookings for group classes. `initialDate` opens a
 * given date (e.g. a pending roll call) instead of the latest one.
 */
const ActivityClassRollCall = ({ classId, initialDate, onSaved }) => {
  const dismissModal = useDismissModal()
  // The date shown in the select, and the one explicitly asked for: the
  // first load asks for none and the backend answers with the latest date.
  const [classDate, setClassDate] = useState(null)
  const [requestedDate, setRequestedDate] = useState(initialDate || null)
  const [rollCall, setRollCall] = useState(null)
  const [present, setPresent] = useState(new Set())
  const [isLoading, setLoading] = useState(true)
  const [isSaving, setSaving] = useState(false)
  // Students picked as "aluno avulso" for this date, not yet saved: cleared
  // whenever the roll call is (re)loaded or successfully saved, since the
  // saved ones come back from the backend as regular `students` entries.
  const [adHocEntries, setAdHocEntries] = useState({})
  const [isAddingStudent, setAddingStudent] = useState(false)
  const [newStudentId, setNewStudentId] = useState('')
  const [newStudentSearch, setNewStudentSearch] = useState('')
  const [newStudentReason, setNewStudentReason] = useState('')
  const { data: studentOptions, setFilters: setStudentFilters } = useDjangoList(
    'users',
    'user',
    { select_return: true, paginate_by: 5 }
  )

  useEffect(() => {
    setStudentFilters((prev) => ({ ...prev, text_filter: newStudentSearch }))
  }, [newStudentSearch, setStudentFilters])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    HttpHandler.getActivityClassRollCall(classId, requestedDate)
      .then((data) => {
        if (cancelled || !data) return
        setRollCall(data)
        setPresent(
          new Set(data.students.filter((s) => s.present).map((s) => s.user_id))
        )
        setClassDate(data.class_date)
        setAdHocEntries({})
      })
      .catch(() => {
        if (!cancelled) setRollCall(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [classId, requestedDate])

  const students = useMemo(() => {
    const enrolled = rollCall?.students || []
    const pendingAdHoc = Object.entries(adHocEntries)
      .filter(([userId]) => !enrolled.some((s) => String(s.user_id) === userId))
      .map(([userId, entry]) => ({
        user_id: Number(userId),
        name: entry.name,
        present: true,
        inclusion_reason: entry.reason,
        isPendingAdHoc: true,
      }))
    return [...enrolled, ...pendingAdHoc].sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }, [rollCall, adHocEntries])
  const allPresent =
    students.length > 0 && students.every((s) => present.has(s.user_id))

  const cancelAddStudent = () => {
    setAddingStudent(false)
    setNewStudentId('')
    setNewStudentSearch('')
    setNewStudentReason('')
  }

  const confirmAddStudent = () => {
    const option = (studentOptions || []).find(
      (candidate) => String(candidate.value) === String(newStudentId)
    )
    if (!option || !newStudentReason) return
    const userId = Number(newStudentId)
    setAdHocEntries((prev) => ({
      ...prev,
      [userId]: { name: option.label, reason: newStudentReason },
    }))
    setPresent((prev) => new Set(prev).add(userId))
    cancelAddStudent()
  }

  const removeAdHocStudent = (userId) => {
    setAdHocEntries((prev) => {
      const next = { ...prev }
      delete next[userId]
      return next
    })
    setPresent((prev) => {
      const next = new Set(prev)
      next.delete(userId)
      return next
    })
  }

  const toggle = (userId) =>
    setPresent((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })

  const toggleAll = () =>
    setPresent(allPresent ? new Set() : new Set(students.map((s) => s.user_id)))

  const save = () => {
    setSaving(true)
    const adHocEntriesPayload = Object.entries(adHocEntries)
      .filter(([userId]) => present.has(Number(userId)))
      .map(([userId, entry]) => ({
        user_id: Number(userId),
        reason: entry.reason,
      }))
    toast
      .promise(
        HttpHandler.saveActivityClassRollCall(
          classId,
          classDate,
          Array.from(present),
          adHocEntriesPayload
        ),
        {
          pending: 'Salvando chamada...',
          success: 'Chamada salva com sucesso!',
          error: 'Falha ao salvar chamada',
        }
      )
      .then((data) => {
        // Lists showing attendance (the "Frequência" reports) reload.
        ROLL_CALL_REPORT_MODELS.forEach((model) =>
          window.dispatchEvent(getModelChangeEvent('activities', model))
        )
        if (onSaved) onSaved(data)
        dismissModal()
      })
      .catch(() => {})
      .finally(() => setSaving(false))
  }

  if (!rollCall && isLoading) return <Loading />
  if (!rollCall)
    return <p className="text-muted">Não foi possível carregar a chamada.</p>

  return (
    <div className="t-roll-call">
      <Select
        label="Data da aula"
        name="class_date"
        className="mb-3 t-field-class_date"
        value={classDate || ''}
        onChange={({ target: { value } }) => {
          setClassDate(value)
          setRequestedDate(value)
        }}
      >
        {/* A date older than the recent ones (`initialDate`) is listed too. */}
        {(rollCall.recent_dates.includes(rollCall.class_date)
          ? rollCall.recent_dates
          : [rollCall.class_date, ...rollCall.recent_dates]
        ).map((date) => (
          <MenuItem key={date} value={date}>
            {formatClassDate(date)}
          </MenuItem>
        ))}
      </Select>

      <div className="flex-between flex-center-v mt-3 mb-2">
        <span className="t-roll-call-summary">
          <b>{present.size}</b> de {students.length} presentes
        </span>
        {students.length > 0 && (
          <Button
            size="small"
            color="primary"
            onClick={toggleAll}
            disabled={isLoading}
          >
            {allPresent ? 'Desmarcar todos' : 'Marcar todos'}
          </Button>
        )}
      </div>

      {isLoading ? (
        <Loading />
      ) : students.length === 0 ? (
        <p className="text-muted my-4">
          {rollCall.activity_class?.is_bookable_class
            ? 'Nenhum aluno agendado para esta data.'
            : 'Nenhum aluno matriculado nesta turma nesta data.'}
        </p>
      ) : (
        students.map((student) => {
          const isPresent = present.has(student.user_id)
          return (
            <div
              key={student.user_id}
              className={`card flex-center-v p-2 mb-1 border-radius-4 t-roll-call-student ${
                isPresent ? 't-roll-call-present' : ''
              } ${student.inclusion_reason ? 't-roll-call-ad-hoc' : ''}`}
              style={{
                minHeight: 48,
                // Present rows stand out at a glance on the phone
                backgroundColor: isPresent
                  ? 'rgba(25, 118, 210, 0.08)'
                  : undefined,
              }}
            >
              <label
                className="flex-center-v"
                style={{ cursor: 'pointer', flex: 1 }}
              >
                <Checkbox
                  checked={isPresent}
                  onChange={() => toggle(student.user_id)}
                  inputProps={{ 'aria-label': student.name }}
                />
                <span>{student.name}</span>
                {student.inclusion_reason && (
                  <span className="badge badge-primary ml-2 t-roll-call-ad-hoc-reason">
                    {AD_HOC_REASON_LABELS[student.inclusion_reason] ||
                      student.inclusion_reason}
                  </span>
                )}
              </label>
              {student.isPendingAdHoc && (
                <Button
                  size="small"
                  onClick={() => removeAdHocStudent(student.user_id)}
                  aria-label={`Remover ${student.name}`}
                >
                  <Icon fontSize="small">close</Icon>
                </Button>
              )}
            </div>
          )
        })
      )}

      {rollCall.can_include_ad_hoc_student &&
        (isAddingStudent ? (
          <div className="card p-2 mb-2 border-radius-4 t-roll-call-add-ad-hoc-student">
            <Select
              label="Aluno"
              name="ad_hoc_student"
              data-djangoapp="users"
              data-djangomodel="user"
              className="mb-2 t-field-ad-hoc-student"
              value={newStudentId}
              onChange={({ target: { value } }) => setNewStudentId(value)}
              options={studentOptions}
            >
              <SearchField onChange={setNewStudentSearch} />
            </Select>
            <Select
              label="Motivo"
              name="ad_hoc_reason"
              className="mb-2 t-field-ad-hoc-reason"
              value={newStudentReason}
              onChange={({ target: { value } }) => setNewStudentReason(value)}
              options={AD_HOC_REASON_OPTIONS}
            />
            <div className="flex-end">
              <Button onClick={cancelAddStudent}>Cancelar</Button>
              <Button
                color="primary"
                disabled={!newStudentId || !newStudentReason}
                onClick={confirmAddStudent}
              >
                Incluir
              </Button>
            </div>
          </div>
        ) : (
          <Button
            className="mb-2 t-button-add-ad-hoc-student"
            color="primary"
            onClick={() => setAddingStudent(true)}
            disabled={isLoading}
          >
            + Incluir aluno avulso
          </Button>
        ))}

      <Button
        fullWidth
        className="mt-3"
        variant="contained"
        color="primary"
        onClick={save}
        disabled={isLoading || isSaving || !classDate}
      >
        Salvar chamada
      </Button>
    </div>
  )
}

export default ActivityClassRollCall
